import {
  ApiAccessChartWidget,
  DatePeriodTypesEnum,
  Flex,
  getPeriodRangeByPeriodType,
  Spinner,
  Text,
} from "dash-ui";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  useGetPluginUserOwnMonthlyApiUsageQuery,
  useGetPluginUserOwnTotalApiUsageQuery,
} from "gql/generated.graphql";

const MAX_API_REQUESTS = 10000;

export const ApiUsageSection = ({ appId }: { appId: string | null }) => {
  const theme = useTheme();
  const { t } = useTranslation(["pages"]);

  const currentMonth = moment().month();

  const { data, loading, error } = useGetPluginUserOwnMonthlyApiUsageQuery({
    variables: {
      targetMonth: currentMonth,
    },
    skip: !appId,
  });

  const reqCount = data?.getPluginUserOwnMonthlyApiUsage;

  const usagePercentage = reqCount ? (reqCount / MAX_API_REQUESTS) * 100 : 0;

  const usagePercentageStyleWidth =
    usagePercentage > 6 || usagePercentage === 0 ? usagePercentage : 6;

  return (
    <Flex mt={4} flexDirection="column">
      <Flex alignItems="center">
        <Text semibold>{t("pages:profilePage:api:apiUsage")}</Text>
      </Flex>
      <Flex mt={0.5} mb={2} alignItems="center">
        <Text color={theme.colors.n70}>Requests this month: </Text>
      </Flex>
      {loading ? (
        <Flex w="100%" alignItems="center" justifyContent="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <p className="mt-2" style={{ color: theme.colors.n70 }}>
          There was an error getting request metrics. Please refresh the page or
          contact support if the error persists.
        </p>
      ) : (
        <>
          {!!reqCount || reqCount === 0 ? (
            <Flex w={"100%"} flexDirection="column">
              <Flex w={"100%"} maxWidth={500}>
                <Flex
                  w={"100%"}
                  className="border rounded-xl border-gray-300 h-6"
                >
                  <Flex
                    h={"100%"}
                    className="rounded-xl"
                    style={{
                      width: `${usagePercentageStyleWidth}%`,
                      backgroundColor: theme.colors.b50,
                    }}
                  />
                </Flex>
                <Flex className="ml-2" flexWrap="nowrap">
                  <p
                    className="text-nowrap"
                    style={{ color: theme.colors.n70 }}
                  >
                    {reqCount} / 10,000
                  </p>
                </Flex>
              </Flex>
              <Flex
                mt={2}
                onClick={() =>
                  window.open("https://help.dashtodash.com", "_blank")
                }
              >
                <a
                  href="https://help.dashtodash.com"
                  target="_blank"
                  className="text-xs underline"
                  style={{ color: theme.colors.n80 }}
                >
                  Need more requests? Click here
                </a>
              </Flex>
            </Flex>
          ) : (
            <p className="mt-2" style={{ color: theme.colors.n70 }}>
              There was an error getting request metrics. Please refresh the
              page or contact support if the error persists.
            </p>
          )}
          <Flex mt={8} w={"100%"} maxWidth={800}>
            <ApiAccessChartWidget
              title={t("pages:profilePage:api:apiActivity")}
              hidePeriodTypes={[
                DatePeriodTypesEnum.CURRENT_YEAR,
                DatePeriodTypesEnum.CUSTOM,
              ]}
              fetchFunction={(dateRange: any) => {
                const { data, loading } = useGetPluginUserOwnTotalApiUsageQuery(
                  {
                    variables: {
                      pagination: {
                        allowLimit: false,
                      },
                      filter: {
                        range: dateRange
                          ? {
                              startDate: dateRange?.startDate,
                              endDate: dateRange?.endDate,
                            }
                          : getPeriodRangeByPeriodType(
                              DatePeriodTypesEnum.CURRENT_WEEK
                            ),
                      },
                    },
                  }
                );

                return {
                  data: data?.getPluginUserOwnTotalApiUsage?.collection || [],
                  loading,
                };
              }}
            />
          </Flex>
          <p
            className="text-sm italic mt-4"
            style={{ color: theme.colors.n70 }}
          >
            *Usage information may be delayed by up to 1 hour.
          </p>
        </>
      )}
    </Flex>
  );
};
